import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useOrder } from '../../contexts/OrderContext';
import { useMembership } from '../../contexts/MembershipContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import MembershipStatus from '../../components/membership/MembershipStatus';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';
import formatOrderId from '../../utils/formatOrderId';

const { FiPlus, FiClock, FiCheckCircle, FiAlertCircle, FiFileText, FiTrendingUp, FiUsers, FiStar, FiHome, FiMonitor } = FiIcons;

const CustomerDashboard = () => {
  const { user, extendSession, isSessionExpiringSoon } = useAuth();
  const { orders, setOrders } = useOrder();
  const { getMembershipDetails, loading: membershipLoading } = useMembership();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalApplications: 0,
    pendingApplications: 0,
    completedApplications: 0,
    totalSaved: 0
  });
  const [membershipDetails, setMembershipDetails] = useState(null);
  const [showSessionWarning, setShowSessionWarning] = useState(false);

  useEffect(() => {
    // Extend session on component mount and user activity
    extendSession();
    
    // Check if session is expiring soon
    if (isSessionExpiringSoon()) {
      setShowSessionWarning(true);
    }

    // Set up activity listeners to extend session
    const handleActivity = () => {
      extendSession();
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keypress', handleActivity);
    window.addEventListener('click', handleActivity);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keypress', handleActivity);
      window.removeEventListener('click', handleActivity);
    };
  }, [extendSession, isSessionExpiringSoon]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user?.id && !user?.email) return;
      
      try {
        // Fetch from available_orders_a7b5c9 table
        const { data, error } = await supabase
          .from('available_orders_a7b5c9')
          .select('*')
          .or(`customer_email.eq.${user.email},customer_phone.eq.${user.phone}`)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching orders:', error);
          throw error;
        }

        if (data) {
          // Transform data to match expected format
          const transformedOrders = data.map(order => ({
            id: order.id,
            orderId: order.order_id,
            serviceType: order.service_type,
            status: order.status,
            totalAmount: (Number(order.application_fee) || 0) + (Number(order.service_fee) || 0),
            amount: (Number(order.application_fee) || 0) + (Number(order.service_fee) || 0),
            created_at: order.created_at,
            updated_at: order.updated_at,
            payment_status: order.payment_status || 'completed',
            paymentDate: order.created_at,
            customer_name: order.customer_name,
            customer_email: order.customer_email,
            customer_phone: order.customer_phone,
            documents: order.documents,
            submitted_pdf_url: order.submitted_pdf_url,
            verified_at: order.verified_at
          }));
          
          setOrders(transformedOrders);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      }
    };

    fetchOrders();
    
    // Set up realtime subscription for order updates
    const channel = supabase
      .channel('customer-orders-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'available_orders_a7b5c9',
          filter: `customer_email=eq.${user?.email}`
        },
        (payload) => {
          console.log('Realtime order update:', payload);
          // Refresh orders when changes occur
          fetchOrders();
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, user?.email, user?.phone, setOrders]);

  useEffect(() => {
    // Calculate stats from orders
    const totalApplications = orders.length;
    const pendingApplications = orders.filter(order => 
      ['available', 'assigned', 'in_progress', 'Processing'].includes(order.status)
    ).length;
    const completedApplications = orders.filter(order => order.status === 'completed').length;
    const totalSaved = completedApplications * 2; // Assuming 2 hours saved per application

    setStats({
      totalApplications,
      pendingApplications,
      completedApplications,
      totalSaved
    });
  }, [orders]);

  useEffect(() => {
    // Get membership details if available
    const details = getMembershipDetails();
    if (details) {
      setMembershipDetails(details);
    }
  }, [getMembershipDetails]);

  const recentOrders = orders.slice(0, 3);

  const handleNewApplication = () => {
    // Always clear any previous service type selection for new applications
    localStorage.removeItem('selectedServiceType');
    
    // Always navigate to service type selection first
    navigate('/service-type-selection');
  };

  const quickActions = [
    {
      title: 'New Application',
      description: 'Start a new application process',
      icon: FiPlus,
      onClick: handleNewApplication,
      color: 'bg-primary-600 hover:bg-primary-700'
    },
    {
      title: 'Upload Documents',
      description: 'Upload required documents',
      icon: FiFileText,
      link: '/upload-documents',
      color: 'bg-green-600 hover:bg-green-700'
    },
    {
      title: 'Membership Plans',
      description: 'Save money with our plans',
      icon: FiStar,
      link: '/membership',
      color: 'bg-yellow-600 hover:bg-yellow-700'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'pending_payment': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return FiCheckCircle;
      case 'in_progress': return FiClock;
      case 'pending_payment': return FiAlertCircle;
      default: return FiClock;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Session Warning */}
        {showSessionWarning && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="p-4 bg-yellow-50 border-yellow-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiAlertCircle} className="w-6 h-6 text-yellow-600" />
                  <div>
                    <h3 className="font-semibold text-yellow-900">Session Expiring Soon</h3>
                    <p className="text-sm text-yellow-800">Your session will expire in less than an hour. Any activity will extend your session.</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    extendSession();
                    setShowSessionWarning(false);
                  }}
                  className="text-yellow-700 border-yellow-300 hover:bg-yellow-100"
                >
                  Extend Session
                </Button>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-4 sm:mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {user?.name}!
          </h1>
          <p className="text-gray-600 mt-2">
            Manage your applications and track progress from your dashboard
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <Card className="p-6">
              <div className="flex items-center">
                <div className="bg-primary-100 p-3 rounded-lg">
                  <SafeIcon icon={FiFileText} className="w-6 h-6 text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Applications</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalApplications}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="p-6">
              <div className="flex items-center">
                <div className="bg-yellow-100 p-3 rounded-lg">
                  <SafeIcon icon={FiClock} className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.pendingApplications}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Card className="p-6">
              <div className="flex items-center">
                <div className="bg-green-100 p-3 rounded-lg">
                  <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.completedApplications}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Card className="p-6">
              <div className="flex items-center">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Time Saved</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalSaved}h</p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {/* Membership Status */}
            {membershipDetails && !membershipLoading && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="mb-8"
              >
                <MembershipStatus membershipDetails={membershipDetails} />
              </motion.div>
            )}

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Card className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {quickActions.map((action, index) => (
                    <div key={index}>
                      {action.onClick ? (
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={action.onClick}
                          className={`${action.color} text-white p-6 rounded-lg transition-colors cursor-pointer`}
                        >
                          <SafeIcon icon={action.icon} className="w-8 h-8 mb-3" />
                          <h3 className="font-semibold mb-1">{action.title}</h3>
                          <p className="text-sm opacity-90">{action.description}</p>
                        </motion.div>
                      ) : (
                        <Link to={action.link}>
                          <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`${action.color} text-white p-6 rounded-lg transition-colors cursor-pointer`}
                          >
                            <SafeIcon icon={action.icon} className="w-8 h-8 mb-3" />
                            <h3 className="font-semibold mb-1">{action.title}</h3>
                            <p className="text-sm opacity-90">{action.description}</p>
                          </motion.div>
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>

            {/* Recent Orders */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-8"
            >
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Recent Applications</h2>
                </div>

                {recentOrders.length > 0 ? (
                  <div className="space-y-4">
                    {recentOrders.map((order) => (
                      <div key={order.id} className="p-4 bg-gray-50 rounded-lg">
                        {/* Header row */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <SafeIcon
                              icon={getStatusIcon(order.status)}
                              className={`w-5 h-5 ${getStatusColor(order.status).split(' ')[0]}`}
                            />
                            <div>
                              <p className="font-medium text-gray-900">{order.serviceType || 'Service'}</p>
                              <p className="text-sm text-gray-600">Order #<span className="font-mono">{formatOrderId(order.orderId || order.id, { date: order.created_at })}</span></p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                              {(order.status || 'unknown').replace('_', ' ')}
                            </span>
                            <p className="text-sm text-gray-600 mt-1">₹{order.totalAmount ?? order.amount ?? '—'}</p>
                          </div>
                        </div>

                        {/* Details grid */}
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                          <div className="bg-white rounded-md p-2 border border-gray-200">
                            <p className="text-gray-500">Created</p>
                            <p className="font-medium text-gray-900">{order.created_at ? new Date(order.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}</p>
                          </div>
                          <div className="bg-white rounded-md p-2 border border-gray-200">
                            <p className="text-gray-500">Payment Status</p>
                            <p className="font-medium text-gray-900">{order.payment_status || 'Completed'}</p>
                          </div>
                          <div className="bg-white rounded-md p-2 border border-gray-200">
                            <p className="text-gray-500">Status</p>
                            <p className="font-medium text-gray-900 capitalize">{(order.status || 'pending').replace('_', ' ')}</p>
                          </div>
                          <div className="bg-white rounded-md p-2 border border-gray-200">
                            <p className="text-gray-500">Order ID</p>
                            <p className="font-medium text-gray-900 font-mono">{formatOrderId(order.orderId || order.id, { date: order.created_at })}</p>
                          </div>
                          <div className="bg-white rounded-md p-2 border border-gray-200">
                            <p className="text-gray-500">Updated</p>
                            <p className="font-medium text-gray-900">{order.updated_at ? new Date(order.updated_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'N/A'}</p>
                          </div>
                          {order.verified_at && (
                            <div className="bg-white rounded-md p-2 border border-gray-200">
                              <p className="text-gray-500">Verified</p>
                              <p className="font-medium text-gray-900">{new Date(order.verified_at).toLocaleString('en-IN', { day: 'numeric', month: 'short' })}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    <div className="mt-6 space-y-3">
                      <h3 className="text-sm font-medium text-gray-900">Quick Actions</h3>
                      {recentOrders.map((order) => (
                        <div key={`action-${order.id}`} className="flex items-center justify-between flex-col p-3 bg-white border border-gray-200 rounded-lg">
                          <div className="flex items-center space-x-3 mb-3">
                            <SafeIcon icon={FiFileText} className="w-4 h-4 text-gray-500" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{order.serviceType}</p>
                              <p className="text-xs text-gray-500">Order #<span className="font-mono">{formatOrderId(order.orderId || order.id, { date: order.created_at })}</span></p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => {
                              const oid = order.orderId || order.id;
                              console.log('Tracking order:', oid);
                              try {
                                // Normalize and persist order for OrderTracking fallback
                                const normalized = {
                                  ...order,
                                  orderId: oid,
                                  // Backward-compat fields used in OrderTracking sidebar/timeline
                                  totalAmount: order.totalAmount ?? order.amount ?? 0,
                                  paymentDate: order.paymentDate || order.created_at || order.createdAt,
                                  serviceType: order.serviceType || order.service_type,
                                };
                                sessionStorage.setItem('currentOrder', JSON.stringify(normalized));
                              } catch (e) {
                                // non-blocking
                              }
                              navigate(`/track-order/${oid}`);
                            }}
                            className="flex items-center space-x-1"
                          >
                            <SafeIcon icon={FiFileText} className="w-4 h-4" />
                            <span>Track Application</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <SafeIcon icon={FiFileText} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No applications yet</p>
                    <p className="text-sm text-gray-500 mb-4">Start your first application to see it here</p>
                    <Button onClick={handleNewApplication}>
                      <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                      New Application
                    </Button>
                  </div>
                )}
              </Card>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Service Type Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Types</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiHome} className="w-6 h-6 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900">Home Services</h4>
                      <p className="text-sm text-gray-600">Professional operators visit your home</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiMonitor} className="w-6 h-6 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900">Online Services</h4>
                      <p className="text-sm text-gray-600">Complete applications online with document upload</p>
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleNewApplication}
                  className="w-full mt-4 border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  Start New Application
                </Button>
              </Card>
            </motion.div>

            {/* Membership Promotion (only show if user doesn't have membership) */}
            {!membershipDetails && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                <Card className="p-6 bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
                  <div className="text-center">
                    <SafeIcon icon={FiStar} className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Upgrade to Premium</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Save money with our membership plans. Apply to unlimited applications with just the application fee!
                    </p>
                    <Link to="/membership">
                      <Button className="w-full bg-yellow-600 hover:bg-yellow-700">
                        View Plans
                      </Button>
                    </Link>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Support */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiUsers} className="w-5 h-5 text-primary-600" />
                    <span className="text-sm text-gray-700">Customer Care Services from 8 AM to 11 PM</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-gray-700">Zero Mistakes Policy</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiStar} className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm text-gray-700">Expert Operators</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full mt-4">
                  Contact Support
                </Button>
              </Card>
            </motion.div>

            {/* Membership Benefits (if user has membership) */}
            {membershipDetails && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.9 }}
              >
                <Card className="p-6 bg-gradient-to-br from-green-50 to-blue-50">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Membership Benefits</h3>
                  <ul className="space-y-2">
                    {membershipDetails.plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <Link to="/membership">
                      <Button variant="outline" className="w-full">
                        Manage Membership
                      </Button>
                    </Link>
                  </div>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;